import { useEffect, useState, useRef } from "react";
import Swal from 'sweetalert2';
import { useDataUser } from "../../context/dataUserContext";
import { validarInputExpresionRegular } from "../../utils/generalFunctions";
import { alertaCargandoProceso, alertaGeneral } from "../../utils/alertas";
import apiClient from "../../utils/axiosPetition";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import InputFile from "../../utils/InputFile";
import ImageCropper from "../ImageCropper/ImageCropper";
import SignatureCanvas from 'react-signature-canvas';
import { useMembership } from "../../context/MembershipContext";
import ContratoVinculacionPlanMiembroFree from './ContratoVinculacionPlanMiembroFree';
import ContratoVinculacionPlanMiembroPremium from './ContratoVinculacionPlanMiembroPremium';
import { SignatureHelpModal, SignatureInstructionsPanel } from './SignatureHelp';
import logoRedplika from '../../images/salutioRed.png';

const FILE_KEYS = ['foto_perfil', 'cedulaPDF', 'diploma_acta_grado', 'tarjeta_profesional', 'firma_profesional', 'rethus', 'poliza_responsabilidad_civil', 'diploma_especializacion', 'homologacion_estudios'];

// Precio de controles: barra $20.000 - $50.000, pasos $5.000 (igual que /doctor/perfil)
const PRECIO_CONTROLES_MIN = 20000;
const PRECIO_CONTROLES_SLIDER_MAX = 50000;
const PRECIO_CONTROLES_MAX_TERAPEUTA_OCUPACIONAL = 40000;
const PRECIO_CONTROLES_MAX_MEDICINA_FAMILIAR = 90000;
const PRECIO_CONTROLES_MAX_MEDICINA_GENERAL = 60000;
const PRECIO_CONTROLES_STEP = 5000;

const DRAFT_DB_NAME = 'DoctorPerfilDrafts';
const DRAFT_DB_STORE = 'files';

function openDraftDB() {
    return new Promise((resolve, reject) => {
        const r = indexedDB.open(DRAFT_DB_NAME, 1);
        r.onerror = () => reject(r.error);
        r.onsuccess = () => resolve(r.result);
        r.onupgradeneeded = (e) => { e.target.result.createObjectStore(DRAFT_DB_STORE); };
    });
}

function saveDraftFile(draftKey, fieldName, file) {
    if (!file || !(file instanceof File)) return Promise.resolve();
    return openDraftDB().then(db => new Promise((resolve, reject) => {
        const tx = db.transaction(DRAFT_DB_STORE, 'readwrite');
        tx.objectStore(DRAFT_DB_STORE).put({ name: file.name, type: file.type, blob: file }, `${draftKey}_${fieldName}`);
        tx.oncomplete = () => { db.close(); resolve(); };
        tx.onerror = () => reject(tx.error);
    }));
}

function getDraftFile(draftKey, fieldName) {
    return openDraftDB().then(db => new Promise((resolve, reject) => {
        const tx = db.transaction(DRAFT_DB_STORE, 'readonly');
        const req = tx.objectStore(DRAFT_DB_STORE).get(`${draftKey}_${fieldName}`);
        req.onsuccess = () => {
            db.close();
            const row = req.result;
            if (!row || !row.blob) return resolve(null);
            resolve(new File([row.blob], row.name || 'file', { type: row.type || row.blob.type }));
        };
        req.onerror = () => reject(req.error);
    }));
}

function clearDraftFiles(draftKey) {
    if (!draftKey) return Promise.resolve();
    return openDraftDB().then(db => new Promise((resolve, reject) => {
        const tx = db.transaction(DRAFT_DB_STORE, 'readwrite');
        const store = tx.objectStore(DRAFT_DB_STORE);
        tx.oncomplete = () => { db.close(); resolve(); };
        tx.onerror = () => reject(tx.error);
        const range = IDBKeyRange.bound(draftKey, draftKey + '\uffff');
        const req = store.getAllKeys(range);
        req.onsuccess = () => {
            (req.result || []).forEach(k => store.delete(k));
        };
        req.onerror = () => reject(req.error);
    }));
}

const DRAFT_METADATA_KEYS = ['_doctorId', '_hasPolicy', '_hasFile'];
/** Campos que vienen precargados del perfil del doctor o tienen valores por defecto; si el borrador solo tiene estos, no se considera "dato guardado" por el usuario. */
const DRAFT_PRELOADED_KEYS = ['fullName', 'idType', 'idNumber', 'email', 'cellNumber'];

/** Solo considerar "borrador" si tiene archivos o al menos un campo con dato que el usuario haya añadido (evita "Borrador encontrado" al entrar y salir sin llenar nada). */
function isDraftMeaningful(parsed) {
    if (!parsed || typeof parsed !== 'object') return false;
    const hasFiles = parsed._hasFile && Object.keys(parsed._hasFile).length > 0;
    if (hasFiles) return true;
    for (const key of Object.keys(parsed)) {
        if (DRAFT_METADATA_KEYS.includes(key) || DRAFT_PRELOADED_KEYS.includes(key)) continue;
        const v = parsed[key];
        if (v === null || v === undefined) continue;
        if (typeof v === 'string' && v.trim() === '') continue;
        if (typeof v === 'number' && !Number.isNaN(v)) return true;
        if (typeof v === 'string' && v.trim() !== '') return true;
    }
    return false;
}

const CompleterProfileDoctor = ({ doctor, setDoctor, setShow }) => {
    const { membershipStatus, loading: membershipLoading } = useMembership();
    const doctorServiceStr = (doctor?.service && String(doctor.service)) || '';
    const draftStorageKey = doctor?._id ? `doctor_perfil_draft_${doctor._id}` : null;
    const [departments, setDepartments] = useState([]);
    const [cities, setCities] = useState([]);
    const [hasPolicy, setHasPolicy] = useState('');
    const [showContactField, setShowContactField] = useState(false);
    const [contactNumber, setContactNumber] = useState('');
    const [showImageCropper, setShowImageCropper] = useState(false);
    const [imageToCrop, setImageToCrop] = useState(null);
    const fileInputRef = useRef(null);
    const sigCanvasRefContrato = useRef(null);
    const signatureContainerRef = useRef(null);
    const [contractSigning, setContractSigning] = useState(false);
    const [showContratoModal, setShowContratoModal] = useState(false);
    const [showFirmaInstruccionesModal, setShowFirmaInstruccionesModal] = useState(false);
    const [signatureCanvasSize, setSignatureCanvasSize] = useState({ width: 500, height: 160 });
    const [contractModalNombre, setContractModalNombre] = useState('');
    const [contractModalIdType, setContractModalIdType] = useState('');
    const [contractModalIdNumber, setContractModalIdNumber] = useState('');
    const isPremiumDoctor = membershipStatus?.plan === 'premium';
    const contractType = isPremiumDoctor ? 'premium' : 'free';
    const contractTitle = isPremiumDoctor
        ? 'Contrato de vinculación - Plan Miembro Premium'
        : 'Contrato de vinculación - Plan Miembro Free';
    const signedContractUrl = isPremiumDoctor
        ? doctor?.urlContratoVinculacionPremiumFirmado
        : doctor?.urlContratoVinculacionFirmado;
    const signedContractDate = isPremiumDoctor
        ? doctor?.fechaFirmaContratoVinculacionPremium
        : doctor?.fechaFirmaContratoVinculacion;
    const ContractDocumentComponent = isPremiumDoctor
        ? ContratoVinculacionPlanMiembroPremium
        : ContratoVinculacionPlanMiembroFree;

    // Al abrir el modal del contrato, prellenar nombre e identificación con los datos del doctor si existen
    useEffect(() => {
        if (showContratoModal) {
            setContractModalNombre(doctor?.fullName || '');
            setContractModalIdType(doctor?.idType || '');
            setContractModalIdNumber(doctor?.idNumber || '');
            setShowFirmaInstruccionesModal(true);
        } else {
            setShowFirmaInstruccionesModal(false);
        }
    }, [showContratoModal, doctor?.fullName, doctor?.idType, doctor?.idNumber]);

    // Ajustar tamaño del canvas de firma al contenedor para que se pueda firmar en todo el cuadro
    useEffect(() => {
        if (!showContratoModal) return;
        const updateSize = () => {
            if (signatureContainerRef.current) {
                const w = signatureContainerRef.current.clientWidth || 500;
                const h = 160;
                setSignatureCanvasSize(prev => (prev.width === w && prev.height === h ? prev : { width: w, height: h }));
            }
        };
        updateSize();
        const t1 = setTimeout(updateSize, 50);
        const t2 = setTimeout(updateSize, 300);
        window.addEventListener('resize', updateSize);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            window.removeEventListener('resize', updateSize);
        };
    }, [showContratoModal]);

    // Función para normalizar el servicio
    const normalizeService = (service) => {
        if (!service) return '';
        return service.toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // Quitar tildes
            .replace(/\d+/g, '') // Quitar números
            .replace(/_/g, ' ') // Reemplazar guiones bajos
            .replace(/-/g, ' ') // Reemplazar guiones
            .trim();
    }

    // Función para obtener la configuración de precios según la especialidad
    const getPreciosConfig = () => {
        if (!doctor || !doctor.service) return null;
        
        const serviceNormalized = normalizeService(doctor.service);
        
        // Configuración de precios por especialidad según la tabla
        const preciosConfig = {
            // Medicina familiar: 100k-120k / control máx $90k / 50k
            'medicina familiar': {
                teleconsultaPrimeraVez: { min: 100000, max: 120000 },
                precioControles: { min: PRECIO_CONTROLES_MIN, max: PRECIO_CONTROLES_MAX_MEDICINA_FAMILIAR },
                precioTeleorientacion: 50000
            },
            // Medicina General: 60k-100k / control máx $60k / 45k
            'medicina general': {
                teleconsultaPrimeraVez: { min: 60000, max: 100000 },
                precioControles: { min: PRECIO_CONTROLES_MIN, max: PRECIO_CONTROLES_MAX_MEDICINA_GENERAL },
                precioTeleorientacion: 45000
            },
            // Terapia Física: 30k-50k / control máx $40k / 25k
            'terapia fisica': {
                teleconsultaPrimeraVez: { min: 30000, max: 50000 },
                precioControles: { min: PRECIO_CONTROLES_MIN, max: PRECIO_CONTROLES_MAX_TERAPEUTA_OCUPACIONAL },
                precioTeleorientacion: 25000
            },
            // Terapia de Lenguaje / Terapeuta de Lenguaje: 1ª vez $30k-$50k / control máx $40k / Teleorientación $25k
            'terapia de lenguaje': {
                teleconsultaPrimeraVez: { min: 30000, max: 50000 },
                precioControles: { min: PRECIO_CONTROLES_MIN, max: PRECIO_CONTROLES_MAX_TERAPEUTA_OCUPACIONAL },
                precioTeleorientacion: 25000
            },
            'terapia lenguaje': {
                teleconsultaPrimeraVez: { min: 30000, max: 50000 },
                precioControles: { min: PRECIO_CONTROLES_MIN, max: PRECIO_CONTROLES_MAX_TERAPEUTA_OCUPACIONAL },
                precioTeleorientacion: 25000
            },
            'terapeuta de lenguaje': {
                teleconsultaPrimeraVez: { min: 30000, max: 50000 },
                precioControles: { min: PRECIO_CONTROLES_MIN, max: PRECIO_CONTROLES_MAX_TERAPEUTA_OCUPACIONAL },
                precioTeleorientacion: 25000
            },
            // Terapia Ocupacional / Terapeuta Ocupacional: 30k-50k / control máx $40k / 25k
            'terapia ocupacional': {
                teleconsultaPrimeraVez: { min: 30000, max: 50000 },
                precioControles: { min: PRECIO_CONTROLES_MIN, max: PRECIO_CONTROLES_MAX_TERAPEUTA_OCUPACIONAL },
                precioTeleorientacion: 25000
            },
            'terapeuta ocupacional': {
                teleconsultaPrimeraVez: { min: 30000, max: 50000 },
                precioControles: { min: PRECIO_CONTROLES_MIN, max: PRECIO_CONTROLES_MAX_TERAPEUTA_OCUPACIONAL },
                precioTeleorientacion: 25000
            },
            // Terapia respiratoria: 30k-50k / control máx $40k / 25k
            'terapia respiratoria': {
                teleconsultaPrimeraVez: { min: 30000, max: 50000 },
                precioControles: { min: PRECIO_CONTROLES_MIN, max: PRECIO_CONTROLES_MAX_TERAPEUTA_OCUPACIONAL },
                precioTeleorientacion: 25000
            },
            'terapeuta respiratorio': {
                teleconsultaPrimeraVez: { min: 30000, max: 50000 },
                precioControles: { min: PRECIO_CONTROLES_MIN, max: PRECIO_CONTROLES_MAX_TERAPEUTA_OCUPACIONAL },
                precioTeleorientacion: 25000
            },
            // Psicología: 50k-100k / control máx $60k / 25k
            'psicologia': {
                teleconsultaPrimeraVez: { min: 50000, max: 100000 },
                precioControles: { min: PRECIO_CONTROLES_MIN, max: PRECIO_CONTROLES_MAX_MEDICINA_GENERAL },
                precioTeleorientacion: 25000
            },
            'psicologo': {
                teleconsultaPrimeraVez: { min: 50000, max: 100000 },
                precioControles: { min: PRECIO_CONTROLES_MIN, max: PRECIO_CONTROLES_MAX_MEDICINA_GENERAL },
                precioTeleorientacion: 25000
            },
            // Trabajo Social: 1ª vez $50k-$100k / control máx $60k / Teleorientación $25k
            'trabajo social': {
                teleconsultaPrimeraVez: { min: 50000, max: 100000 },
                precioControles: { min: PRECIO_CONTROLES_MIN, max: PRECIO_CONTROLES_MAX_MEDICINA_GENERAL },
                precioTeleorientacion: 25000
            },
            // Nutrición: 50k-100k / control máx $60k / 25k
            'nutricion': {
                teleconsultaPrimeraVez: { min: 50000, max: 100000 },
                precioControles: { min: PRECIO_CONTROLES_MIN, max: PRECIO_CONTROLES_MAX_MEDICINA_GENERAL },
                precioTeleorientacion: 25000
            },
            'nutricionista': {
                teleconsultaPrimeraVez: { min: 50000, max: 100000 },
                precioControles: { min: PRECIO_CONTROLES_MIN, max: PRECIO_CONTROLES_MAX_MEDICINA_GENERAL },
                precioTeleorientacion: 25000
            },
            // Enfermería: 50k-100k / control máx $60k / 25k
            'enfermeria': {
                teleconsultaPrimeraVez: { min: 50000, max: 100000 },
                precioControles: { min: PRECIO_CONTROLES_MIN, max: PRECIO_CONTROLES_MAX_MEDICINA_GENERAL },
                precioTeleorientacion: 25000
            },
            'enfermero': {
                teleconsultaPrimeraVez: { min: 50000, max: 100000 },
                precioControles: { min: PRECIO_CONTROLES_MIN, max: PRECIO_CONTROLES_MAX_MEDICINA_GENERAL },
                precioTeleorientacion: 25000
            },
            'enfermera': {
                teleconsultaPrimeraVez: { min: 50000, max: 100000 },
                precioControles: { min: PRECIO_CONTROLES_MIN, max: PRECIO_CONTROLES_MAX_MEDICINA_GENERAL },
                precioTeleorientacion: 25000
            }
        };

        // Buscar coincidencia en la configuración
        for (const [key, config] of Object.entries(preciosConfig)) {
            if (serviceNormalized.includes(key)) {
                console.log('✅ Precios config encontrados para:', key, config);
                return config;
            }
        }

        console.log('⚠️ No se encontró configuración de precios para:', serviceNormalized);
        return null;
    }

    // Función para verificar si la especialidad requiere configuración de precios
    const requiereConfiguracionPrecios = () => {
        return getPreciosConfig() !== null;
    }

    const [formData, setFormData] = useState({
        fullName: doctor.fullName || '', // Nombre Completo
        idType: doctor.idType || 'cc', // Tipo de Documento de Identidad
        idNumber: doctor.idNumber || '', // Número de Documento de Identidad
        email: doctor.email || '', // Correo Electrónico
        cellNumber: doctor.cellNumber || '', // Número de Celular
        foto_perfil: '', // Foto profesional digital (siempre presente para permitir cambio)
        ...(!doctor.urlIdDocument && { cedulaPDF: '' }), // Documento Identidad (Adjuntar)
        diploma_acta_grado: '', // Diploma o Acta de Grado de Pregrado (PDF o Imagen)
        tarjeta_profesional: '', // Tarjeta Profesional (PDF o Imagen)
        firma_profesional: '', // Firma profesional digital
        rethus: '', // ReThus (PDF o Imagen)
        civilLiabilityPolicyNumber: '', // Póliza de Responsabilidad Civil (Número)
        poliza_responsabilidad_civil: '', // Póliza de Responsabilidad Civil (PDF o Imagen)
        diploma_especializacion: '', // Diploma o Acta de Grado de Especialización
        homologacion_estudios: '', // Homologación de Estudios
    });

    console.log(formData)

    const [camposOpcionales] = useState(() => {
        const baseOptionalFields = (doctorServiceStr.includes('medicina') || doctorServiceStr.includes('psicología') || doctorServiceStr.includes('terapia') ||
            doctorServiceStr.includes('terapeuta') || doctorServiceStr.includes('nutrición') || doctorServiceStr.includes('enfermería') || doctorServiceStr.includes('fonoaudiología')) ?
            [
                'diploma_especializacion',
                'homologacion_estudios',
            ] :
            [
                'diploma_especializacion',
                'homologacion_estudios',
                'rethus',
                'civilLiabilityPolicyNumber',
                'poliza_responsabilidad_civil',
                'firma_profesional'
            ];
        
        // Si el doctor ya tiene foto profesional (ej. de Google), hacer foto_perfil opcional
        if (doctor.urlProfessionalPhoto && doctor.urlProfessionalPhoto !== '') {
            return [...baseOptionalFields, 'foto_perfil'];
        }
        
        return baseOptionalFields;
    });


    useEffect(() => {

        // Fetch departments when modal opens
        const fetchDepartments = async () => {
            try {
                const response = await apiClient.get(`/api/clinic/departments`);
                setDepartments(response.data);
            } catch (error) {
                console.error('Error fetching departments:', error);
                alertaGeneral({
                    titulo: 'Error al cargar departamentos',
                    messageHtml: 'No se pudieron cargar los departamentos. Por favor, recarga la página e inténtalo de nuevo.',
                    isError: true
                });
            }
        };

        fetchDepartments();

    }, []);

    useEffect(() => {
        if (formData.department) {
            // Fetch municipalities when a department is selected
            const fetchCities = async () => {
                if (formData.department) {
                    try {
                        const response = await apiClient.get(`/api/clinic/cities/${formData.department}`);
                        setCities(response.data);
                    } catch (error) {
                        console.error('Error fetching cities:', error);
                        alertaGeneral({
                            titulo: 'Error al cargar ciudades',
                            messageHtml: 'No se pudieron cargar las ciudades del departamento seleccionado. Por favor, inténtalo de nuevo.',
                            isError: true
                        });
                    }
                }
            };

            fetchCities();
        } else {
            setCities([]);
        }
    }, [formData.department]);

    // Restaurar borrador al montar (si existe y tiene contenido real)
    useEffect(() => {
        if (!draftStorageKey) return;
        try {
            const raw = localStorage.getItem(draftStorageKey);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (parsed._doctorId !== doctor._id) return;
            if (!isDraftMeaningful(parsed)) {
                localStorage.removeItem(draftStorageKey);
                clearDraftFiles(draftStorageKey);
                return;
            }
            const hasFiles = parsed._hasFile && Object.keys(parsed._hasFile).length > 0;
            Swal.fire({
                title: 'Borrador encontrado',
                html: hasFiles
                    ? 'Hay datos y archivos guardados de una sesión anterior. ¿Deseas restaurarlos?'
                    : 'Hay datos guardados de una sesión anterior. ¿Deseas restaurarlos?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sí, restaurar',
                cancelButtonText: 'No, empezar de nuevo',
                customClass: { title: 'swal2-title-custom', htmlContainer: 'swal2-html-container-custom' }
            }).then((result) => {
                if (result.isConfirmed) {
                    const restored = { ...parsed };
                    delete restored._doctorId;
                    delete restored._hasPolicy;
                    delete restored._hasFile;
                    FILE_KEYS.forEach(k => { restored[k] = ''; });
                    setFormData(prev => ({ ...prev, ...restored }));
                    if (parsed._hasPolicy) setHasPolicy(parsed._hasPolicy);
                    const fileKeysToRestore = parsed._hasFile ? Object.keys(parsed._hasFile) : [];
                    if (fileKeysToRestore.length > 0) {
                        Promise.all(fileKeysToRestore.map(k => getDraftFile(draftStorageKey, k)))
                            .then(files => {
                                const byKey = {};
                                fileKeysToRestore.forEach((k, i) => { if (files[i]) byKey[k] = files[i]; });
                                if (Object.keys(byKey).length > 0) setFormData(prev => ({ ...prev, ...byKey }));
                            })
                            .catch(() => {});
                    }
                } else {
                    localStorage.removeItem(draftStorageKey);
                    clearDraftFiles(draftStorageKey);
                }
            });
        } catch (e) {
            localStorage.removeItem(draftStorageKey);
            clearDraftFiles(draftStorageKey);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftStorageKey]);

    // Guardar borrador en localStorage + archivos en IndexedDB (debounced)
    const saveDraftRef = useRef(null);
    useEffect(() => {
        if (!draftStorageKey) return;
        if (saveDraftRef.current) clearTimeout(saveDraftRef.current);
        saveDraftRef.current = setTimeout(async () => {
            try {
                const draft = { _doctorId: doctor._id, _hasPolicy: hasPolicy, _hasFile: {} };
                const filePromises = [];
                for (const key in formData) {
                    const value = formData[key];
                    if (value instanceof File) {
                        draft[key] = null;
                        draft._hasFile[key] = true;
                        filePromises.push(saveDraftFile(draftStorageKey, key, value));
                    } else {
                        draft[key] = value;
                    }
                }
                await Promise.all(filePromises);
                localStorage.setItem(draftStorageKey, JSON.stringify(draft));
            } catch (e) {
                console.warn('No se pudo guardar el borrador:', e);
            }
            saveDraftRef.current = null;
        }, 800);
        return () => { if (saveDraftRef.current) clearTimeout(saveDraftRef.current); };
    }, [formData, hasPolicy, draftStorageKey, doctor._id]);

    // Función para verificar conectividad
    const checkConnectivity = async () => {
        try {
            console.log('🔍 Verificando conectividad con:', process.env.REACT_APP_BACKEND_URL);
            
            // Intentar con el endpoint de health o un endpoint simple
            const healthUrl = `${process.env.REACT_APP_BACKEND_URL}/api/health`;
            console.log('Health check URL:', healthUrl);
            
            const response = await fetch(healthUrl, {
                method: 'GET',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            
            console.log('Health check response:', response.status, response.statusText);
            return response.ok;
        } catch (error) {
            console.warn('Health check failed:', error);
            console.warn('Error details:', {
                name: error.name,
                message: error.message,
                stack: error.stack
            });
            return false;
        }
    };

    // Función para reintentos automáticos
    const retryRequest = async (requestFn, maxRetries = 3, delay = 1000) => {
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                return await requestFn();
            } catch (error) {
                console.log(`Intento ${attempt}/${maxRetries} falló:`, error.message);
                
                if (attempt === maxRetries) {
                    throw error;
                }
                
                // Esperar antes del siguiente intento
                await new Promise(resolve => setTimeout(resolve, delay * attempt));
            }
        }
    };

    const dataURLtoFile = (dataurl, filename) => {
        const arr = dataurl.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) u8arr[n] = bstr.charCodeAt(n);
        return new File([u8arr], filename, { type: mime });
    };

    const handleFirmarContrato = async () => {
        if (!sigCanvasRefContrato.current) return;
        if (sigCanvasRefContrato.current.isEmpty()) {
            alertaGeneral({
                titulo: 'Firma requerida',
                messageHtml: 'Debes dibujar tu firma en el recuadro antes de enviar.',
                isWarning: true
            });
            return;
        }
        const nombreTrim = (contractModalNombre || '').trim();
            const idNumTrim = (contractModalIdNumber || '').trim();
            if (!nombreTrim) {
                alertaGeneral({ titulo: 'Datos requeridos', messageHtml: 'Ingresa tu nombre completo en el formulario del contrato.', isError: true });
                return;
            }
            if (!idNumTrim) {
                alertaGeneral({ titulo: 'Datos requeridos', messageHtml: 'Indique su número de identificación en el modal del contrato.', isError: true });
                return;
            }
            setContractSigning(true);
        try {
            const dataUrl = sigCanvasRefContrato.current.toDataURL('image/png');
            const file = dataURLtoFile(dataUrl, 'firma-contrato.png');
            const formData = new FormData();
            formData.append('signature', file);
            formData.append('nombre', nombreTrim);
            formData.append('idType', (contractModalIdType || '').trim());
            formData.append('idNumber', idNumTrim);
            formData.append('contractType', contractType);
            const res = await apiClient.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/doctor/${doctor._id}/firmar-contrato-vinculacion`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            if (res.data && res.data.doctor) {
                setDoctor(res.data.doctor);
            }
            setShowContratoModal(false);
            alertaGeneral({
                titulo: '¡Tu contrato ha sido firmado!',
                messageHtml: 'Puedes continuar con el siguiente paso para completar tu perfil.',
                confirmButtonText: 'Continuar'
            });
        } catch (err) {
            alertaGeneral({
                titulo: 'Error al firmar',
                messageHtml: err?.response?.data?.message || 'No se pudo guardar la firma. Intente de nuevo.',
                isError: true
            });
        } finally {
            setContractSigning(false);
        }
    };

    /* FUNCION DE SUBIDA */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (membershipLoading) {
            alertaGeneral({
                titulo: 'Espere un momento',
                messageHtml: 'Estamos verificando el plan del profesional para validar el contrato requerido.',
                isWarning: true
            });
            return;
        }
        if (!signedContractUrl || signedContractUrl === '') {
            alertaGeneral({
                titulo: 'Contrato de vinculación requerido',
                messageHtml: `Debes firmar el <strong>${contractTitle}</strong> antes de enviar el perfil. Usa la sección de firma digital al inicio del formulario.`,
                isError: true
            });
            return;
        }
        // clinicId puede no estar en localStorage si el doctor se registró por Google/LinkedIn
        const clinicInfo = localStorage.getItem('clinicId') || doctor?.clinicInfo || '66bd188dc4f464511048ceac';
        
        // Verificar configuración del backend
        if (!process.env.REACT_APP_BACKEND_URL) {
            alertaGeneral({
                titulo: 'Error de configuración',
                messageHtml: 'La URL del backend no está configurada. Por favor, contacta al administrador del sistema.',
                isError: true
            });
            return;
        }
        
        // Validaciones previas al envío
        const validationErrors = validateFormData(formData, camposOpcionales, hasPolicy);
        if (validationErrors.length > 0) {
            alertaGeneral({
                titulo: 'Error de validación',
                messageHtml: validationErrors.join('<br/>'),
                isError: true
            });
            return;
        }

        // Comprimir imágenes antes del envío y validar tamaños
        const formDataToSend = new FormData();
        const imageFields = ['foto_perfil', 'cedulaPDF', 'diploma_acta_grado', 'tarjeta_profesional', 'firma_profesional', 'rethus', 'poliza_responsabilidad_civil', 'diploma_especializacion', 'homologacion_estudios'];
        let totalSize = 0;
        // Límite aumentado para AWS (sin restricciones de Vercel)
        const maxTotalSize = 50 * 1024 * 1024; // 50MB máximo total (límite generoso para AWS)
        
        for (const key in formData) {
            if (key !== 'priceService') {
                const value = formData[key];
                
                // Si foto_perfil está vacío y el doctor ya tiene foto de Google, no enviarlo
                if (key === 'foto_perfil' && (!value || value === '') && doctor.urlProfessionalPhoto && doctor.urlProfessionalPhoto !== '') {
                    continue;
                }
                
                // Si es un archivo de imagen, comprimirlo
                if (imageFields.includes(key) && value instanceof File && value.type.startsWith('image/')) {
                    try {
                        // Usar compresión especial para firmas
                        const compressedFile = key === 'firma_profesional' 
                            ? await compressSignature(value)
                            : await compressImage(value);
                            
                        if (compressedFile) {
                            totalSize += compressedFile.size;
                            formDataToSend.append(key, compressedFile, value.name);
                            console.log(`Archivo ${key} comprimido: ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
                        } else {
                            totalSize += value.size;
                            formDataToSend.append(key, value);
                        }
                    } catch (error) {
                        console.warn(`No se pudo comprimir ${key}:`, error);
                        totalSize += value.size;
                        formDataToSend.append(key, value);
                    }
                } else if (value instanceof File) {
                    totalSize += value.size;
                    formDataToSend.append(key, value);
                } else {
                    formDataToSend.append(key, value);
                }
            }
        }

        // Validar tamaño total
        console.log(`Tamaño total de archivos: ${(totalSize / 1024 / 1024).toFixed(2)}MB`);
        
        if (totalSize > maxTotalSize) {
            // Intentar compresión adicional automática
            console.log('⚠️ Archivos exceden el límite, intentando compresión adicional...');
            
            const compressedFormData = await compressAllFiles(formData);
            const newTotalSize = Array.from(compressedFormData.entries())
                .filter(([key, value]) => value instanceof File)
                .reduce((total, [key, value]) => total + value.size, 0);
            
            console.log(`📊 Tamaño después de compresión adicional: ${(newTotalSize / 1024 / 1024).toFixed(2)}MB`);
            
            if (newTotalSize <= maxTotalSize) {
                console.log('✅ Compresión adicional exitosa, procediendo con envío...');
                // Usar los archivos comprimidos
                for (const [key, value] of compressedFormData.entries()) {
                    formDataToSend.set(key, value);
                }
            } else {
                alertaGeneral({
                    titulo: 'Archivos demasiado grandes',
                    messageHtml: `
                        <div class="text-left">
                            <p class="mb-3"><strong>El tamaño total de los archivos es demasiado grande.</strong></p>
                            <p class="mb-2">Tamaño actual: <strong>${(newTotalSize / 1024 / 1024).toFixed(2)}MB</strong></p>
                            <p class="mb-2">Tamaño máximo permitido: <strong>50MB</strong></p>
                            <p class="mb-2"><strong>Recomendaciones:</strong></p>
                            <ul class="list-disc list-inside space-y-1 text-sm">
                                <li>Reduce manualmente el tamaño de las imágenes (máximo 800px de ancho)</li>
                                <li>Comprime los archivos PDF a menos de 1MB cada uno</li>
                                <li>Usa imágenes con menor resolución</li>
                                <li>Considera subir archivos críticos primero</li>
                            </ul>
                            <p class="mt-3 text-sm text-blue-600">
                                <strong>💡 Tip:</strong> Puedes usar herramientas online para comprimir PDFs antes de subirlos.
                            </p>
                        </div>
                    `,
                    isError: true
                });
                return;
            }
        }
        formDataToSend.append('_id', doctor._id);
        formDataToSend.append('clinicInfo', clinicInfo);
        formDataToSend.append('tipoPerfil', 'asistencial');

        // Debug: Verificar configuración antes del envío
        console.log('🔍 DEBUG - Configuración del envío:');
        console.log('Backend URL:', process.env.REACT_APP_BACKEND_URL);
        console.log('URL completa:', `${process.env.REACT_APP_BACKEND_URL}/api/afiliado/prestar-servicios`);
        console.log('Tamaño total de FormData:', totalSize, 'bytes');
        console.log('Archivos en FormData:', Array.from(formDataToSend.keys()));

        alertaCargandoProceso({
            titulo: 'Procesando archivos',
            messageHtml: 'Comprimiendo imágenes y preparando envío...',
            funcionAsync: async () => {
                try {

                    // Función de envío con reintentos y más logging
                    const sendProfile = () => {
                        console.log('🚀 Enviando petición a:', `${process.env.REACT_APP_BACKEND_URL}/api/afiliado/prestar-servicios`);
                        return apiClient.post(`/api/afiliado/prestar-servicios`, formDataToSend, { timeout: 300000 });
                    };
                    
                    const res = await retryRequest(sendProfile, 3, 2000);
                    
                    console.log('✅ Perfil enviado exitosamente:', res.data);

                    Swal.close();

                    const finalizeProfileSubmit = () => {
                        setDoctor(res.data.afiliado);
                        if (draftStorageKey) {
                            localStorage.removeItem(draftStorageKey);
                            clearDraftFiles(draftStorageKey);
                        }
                        setShow(false);
                        localStorage.setItem('imageProfile', res.data.afiliado.urlProfessionalPhoto);
                        window.location.reload();
                    };

                    const successSwalOptions = {
                        title: '¡Gracias por completar tu perfil!',
                        icon: 'success',
                        confirmButtonText: 'Entendido',
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                        customClass: {
                            title: 'swal2-title-custom',
                            htmlContainer: 'swal2-html-container-custom'
                        }
                    };
                    
                    await Swal.fire({
                        ...successSwalOptions,
                        html: `
                            <div class="text-left">
                                <p class="mb-3">Recibimos tu información y documentos. Nuestro equipo revisará todo y te avisará por correo cuando tu perfil esté aprobado.</p>
                                <p class="mb-4">Después podrás configurar tus tarifas, horarios y activar tu botón de pago para comenzar a atender.</p>
                                <p class="mb-3 font-semibold text-[#205087]">Próximos pasos:</p>
                                <ol class="list-decimal list-inside space-y-2 text-sm text-gray-700">
                                    <li>Revisión de documentos por el equipo REDPLIKA.</li>
                                    <li>Aprobación y notificación por correo.</li>
                                    <li>Configuración de tarifas y horarios.</li>
                                </ol>
                                <p class="text-sm text-gray-600 mt-4">Te avisaremos cuando sea el momento de continuar.</p>
                            </div>
                        `,
                    });

                    finalizeProfileSubmit();
                } catch (error) {
                    console.error('❌ Error al enviar perfil después de reintentos:', error);
                    handleSubmitError(error);
                }
            }
        });
    };

    // Función para comprimir todos los archivos adicionalmente
    const compressAllFiles = async (formData) => {
        const compressedData = new FormData();
        
        for (const [key, value] of Object.entries(formData)) {
            if (value instanceof File && value.type.startsWith('image/')) {
                try {
                    // Usar compresión especial para firmas
                    const compressedFile = key === 'firma_profesional' 
                        ? await compressSignature(value, 600, 0.6)
                        : await compressImage(value, 600, 0.3);
                        
                    if (compressedFile) {
                        compressedData.append(key, compressedFile, value.name);
                        console.log(`📸 ${key} comprimido: ${(compressedFile.size / 1024).toFixed(1)}KB`);
                    } else {
                        compressedData.append(key, value);
                    }
                } catch (error) {
                    console.warn(`No se pudo comprimir ${key}:`, error);
                    compressedData.append(key, value);
                }
            } else {
                compressedData.append(key, value);
            }
        }
        
        return compressedData;
    };

    // Función para comprimir imágenes con compresión más agresiva
    const compressImage = (file, maxWidth = 800, quality = 0.5) => {
        return new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();
            
            img.onload = () => {
                // Calcular nuevas dimensiones manteniendo proporción
                let { width, height } = img;
                
                // Reducir más agresivamente si la imagen es muy grande
                if (width > maxWidth) {
                    height = (height * maxWidth) / width;
                    width = maxWidth;
                }
                
                // Si la imagen es muy grande, reducir aún más
                if (width > 600) {
                    const scale = 600 / width;
                    width = 600;
                    height = height * scale;
                }
                
                canvas.width = width;
                canvas.height = height;
                
                // Configurar calidad de renderizado
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                
                // Dibujar imagen redimensionada
                ctx.drawImage(img, 0, 0, width, height);
                
                // Convertir a blob con compresión más agresiva
                canvas.toBlob((blob) => {
                    // Verificar que el archivo comprimido no sea demasiado grande
                    if (blob && blob.size > 2 * 1024 * 1024) { // Si es mayor a 2MB
                        // Comprimir aún más
                        canvas.toBlob(resolve, 'image/jpeg', 0.4);
                    } else {
                        resolve(blob);
                    }
                }, 'image/jpeg', quality);
            };
            
            img.src = URL.createObjectURL(file);
        });
    };

    // Función especial para comprimir firmas (preserva mejor la calidad)
    const compressSignature = (file, maxWidth = 800, quality = 0.8) => {
        return new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();
            
            img.onload = () => {
                // Calcular nuevas dimensiones manteniendo proporción
                let { width, height } = img;
                
                // Para firmas, mantener mejor resolución
                if (width > maxWidth) {
                    height = (height * maxWidth) / width;
                    width = maxWidth;
                }
                
                canvas.width = width;
                canvas.height = height;
                
                // Configurar fondo blanco para firmas
                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(0, 0, width, height);
                
                // Configurar calidad de renderizado alta para firmas
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                
                // Dibujar imagen redimensionada
                ctx.drawImage(img, 0, 0, width, height);
                
                // Para firmas, usar PNG para preservar mejor la calidad
                canvas.toBlob((blob) => {
                    // Si es muy grande, comprimir como JPEG pero con mejor calidad
                    if (blob && blob.size > 2 * 1024 * 1024) {
                        canvas.toBlob(resolve, 'image/jpeg', 0.7);
                    } else {
                        resolve(blob);
                    }
                }, 'image/png', 0.9);
            };
            
            img.src = URL.createObjectURL(file);
        });
    };

    // Función para validar datos del formulario
    const validateFormData = (data, optionalFields, policyChoice) => {
        const errors = [];
        
        // Si respondió "Sí" a póliza de responsabilidad civil, número y documento son obligatorios
        if (policyChoice === 'si') {
            if (!data.civilLiabilityPolicyNumber || !String(data.civilLiabilityPolicyNumber).trim()) {
                errors.push('• El número de Póliza de Responsabilidad Civil es obligatorio cuando indicas que tienes póliza vigente');
            }
            const hasPolizaFile = data.poliza_responsabilidad_civil && (data.poliza_responsabilidad_civil instanceof File || data.poliza_responsabilidad_civil?.name);
            if (!hasPolizaFile) {
                errors.push('• Debes adjuntar el documento de la Póliza de Responsabilidad Civil vigente cuando indicas que tienes póliza');
            }
        }

        // Validar campos obligatorios (salvo póliza cuando hasPolicy !== 'si', que se valida arriba)
        const skipWhenNoPolicy = (key) => policyChoice === 'no' && (key === 'civilLiabilityPolicyNumber' || key === 'poliza_responsabilidad_civil');
        for (const key in data) {
            if (skipWhenNoPolicy(key)) continue;
            if (!data[key] && !optionalFields.includes(key)) {
                errors.push(`• El campo ${reemplazarKeys()[key]} es obligatorio`);
            }
        }

        // Validar archivos de imagen
        const imageFields = ['foto_perfil', 'cedulaPDF', 'diploma_acta_grado', 'tarjeta_profesional', 'firma_profesional'];
        imageFields.forEach(field => {
            if (data[field] && data[field] instanceof File) {
                // Validar tipo de archivo
                const allowedTypes = field === 'foto_perfil' || field === 'firma_profesional' 
                    ? ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
                    : ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
                
                if (!allowedTypes.includes(data[field].type)) {
                    errors.push(`• El archivo ${reemplazarKeys()[field]} debe ser una imagen (JPG, PNG)${field !== 'foto_perfil' && field !== 'firma_profesional' ? ' o PDF' : ''}`);
                }

                // Validar tamaño (máximo 10MB)
                const maxSize = 10 * 1024 * 1024; // 10MB
                if (data[field].size > maxSize) {
                    errors.push(`• El archivo ${reemplazarKeys()[field]} es muy grande. Máximo 10MB`);
                }
            }
        });

        // Validar email
        if (data.email && !isValidEmail(data.email)) {
            errors.push('• El correo electrónico no tiene un formato válido');
        }

        // Validar número de celular
        if (data.cellNumber && !isValidPhoneNumber(data.cellNumber)) {
            errors.push('• El número de celular debe tener 10 dígitos');
        }

        // Validar número de cédula
        if (data.idNumber && !isValidIdNumber(data.idNumber)) {
            errors.push('• El número de documento debe tener entre 6 y 12 dígitos');
        }

        return errors;
    };

    // Función para manejar errores de envío
    const handleSubmitError = (error) => {
        let errorTitle = 'Error al enviar el perfil';
        let errorMessage = 'Ocurrió un error inesperado. Por favor, inténtalo de nuevo.';

        // Manejar errores específicos del servidor
        if (error.response) {
            const status = error.response.status;
            const serverMessage = error.response.data?.message || error.response.data?.error;

            switch (status) {
                case 413:
                    errorTitle = 'Archivos demasiado grandes';
                    errorMessage = `
                        <div class="text-left">
                            <p class="mb-3"><strong>Los archivos que intentas subir son demasiado grandes.</strong></p>
                            <p class="mb-2">El servidor rechazó la petición porque excede el límite de tamaño permitido.</p>
                            <p class="mb-2"><strong>Soluciones:</strong></p>
                            <ul class="list-disc list-inside space-y-1 text-sm">
                                <li>Reduce el tamaño de las imágenes (máximo 1MB por archivo)</li>
                                <li>Comprime los archivos PDF antes de subirlos</li>
                                <li>Usa imágenes con menor resolución</li>
                                <li>Sube los archivos uno por uno si es necesario</li>
                            </ul>
                            <p class="mt-3 text-sm text-blue-600">
                                <strong>💡 Tip:</strong> Las imágenes se comprimen automáticamente, pero si son muy grandes, 
                                considera reducirlas manualmente antes de subirlas.
                            </p>
                        </div>
                    `;
                    break;
                case 400:
                    errorTitle = 'Datos inválidos';
                    errorMessage = serverMessage || 'Los datos enviados no son válidos. Revisa la información e inténtalo de nuevo.';
                    break;
                case 401:
                    errorTitle = 'Sesión expirada';
                    errorMessage = 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.';
                    // Redirigir al login
                    setTimeout(() => {
                        localStorage.clear();
                        window.location.href = '/login';
                    }, 2000);
                    break;
                case 403:
                    errorTitle = 'Acceso denegado';
                    errorMessage = 'No tienes permisos para realizar esta acción.';
                    break;
                case 422:
                    errorTitle = 'Error de validación';
                    errorMessage = serverMessage || 'Los datos enviados no cumplen con los requisitos. Revisa la información e inténtalo de nuevo.';
                    break;
                case 500:
                    errorTitle = 'Error del servidor';
                    errorMessage = 'Ocurrió un error interno del servidor. Por favor, inténtalo más tarde o contacta al soporte técnico.';
                    break;
                case 503:
                    errorTitle = 'Servicio no disponible';
                    errorMessage = 'El servicio no está disponible temporalmente. Por favor, inténtalo más tarde.';
                    break;
                default:
                    errorMessage = serverMessage || errorMessage;
            }
        } else if (error.code === 'NETWORK_ERROR' || !error.response) {
            // Verificar si es un error CORS o de configuración
            const isCorsError = error.message && (
                error.message.includes('CORS') || 
                error.message.includes('Access-Control-Allow-Origin') ||
                error.message.includes('blocked by CORS policy')
            );
            
            const isConfigError = !process.env.REACT_APP_BACKEND_URL || 
                                 process.env.REACT_APP_BACKEND_URL === 'undefined';
            
            if (isCorsError) {
                errorTitle = 'Error de configuración del servidor (CORS)';
                errorMessage = `
                    <div class="text-left">
                        <p class="mb-3"><strong>Error de configuración del servidor (CORS).</strong></p>
                        <p class="mb-2">El servidor no está configurado correctamente para recibir peticiones desde esta aplicación.</p>
                        <p class="mb-2"><strong>Esto es un problema técnico que debe ser resuelto por el administrador del sistema.</strong></p>
                        <p class="text-sm text-red-600">
                            <strong>Contacta al soporte técnico</strong> e informa sobre este error de CORS.
                        </p>
                    </div>
                `;
            } else if (isConfigError) {
                errorTitle = 'Error de configuración';
                errorMessage = `
                    <div class="text-left">
                        <p class="mb-3"><strong>Error de configuración de la aplicación.</strong></p>
                        <p class="mb-2">La URL del servidor backend no está configurada correctamente.</p>
                        <p class="text-sm text-red-600">
                            <strong>Contacta al soporte técnico</strong> para resolver este problema de configuración.
                        </p>
                    </div>
                `;
            } else {
                errorTitle = 'Error de conexión';
                errorMessage = `
                    <div class="text-left">
                        <p class="mb-3"><strong>No se pudo conectar con el servidor.</strong></p>
                        <p class="mb-2">Posibles causas:</p>
                        <ul class="list-disc list-inside space-y-1 text-sm">
                            <li>Problemas temporales de internet</li>
                            <li>El servidor está temporalmente no disponible</li>
                            <li>Firewall o antivirus bloqueando la conexión</li>
                            <li>Problemas de DNS</li>
                        </ul>
                        <p class="mt-3 text-sm"><strong>Recomendaciones:</strong></p>
                        <ul class="list-disc list-inside space-y-1 text-sm">
                            <li>Verifica tu conexión a internet</li>
                            <li>Intenta recargar la página</li>
                            <li>Espera unos minutos e inténtalo de nuevo</li>
                            <li>Si el problema persiste, contacta al soporte técnico</li>
                        </ul>
                    </div>
                `;
            }
        } else if (error.response) {
            const status = error.response.status;
            const serverMessage = error.response.data?.message || error.response.data?.error;

            switch (status) {
                case 400:
                    errorTitle = 'Datos inválidos';
                    errorMessage = serverMessage || 'Los datos enviados no son válidos. Revisa la información e inténtalo de nuevo.';
                    break;
                case 401:
                    errorTitle = 'Sesión expirada';
                    errorMessage = 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.';
                    // Redirigir al login
                    setTimeout(() => {
                        localStorage.clear();
                        window.location.href = '/login';
                    }, 2000);
                    break;
                case 403:
                    errorTitle = 'Acceso denegado';
                    errorMessage = 'No tienes permisos para realizar esta acción.';
                    break;
                case 413:
                    errorTitle = 'Archivo muy grande';
                    errorMessage = 'Uno o más archivos son demasiado grandes. El tamaño máximo permitido es 2MB por archivo. Si tienes archivos grandes, comprímelos antes de subirlos.';
                    break;
                case 422:
                    errorTitle = 'Error de validación';
                    errorMessage = serverMessage || 'Los datos enviados no cumplen con los requisitos. Revisa la información e inténtalo de nuevo.';
                    break;
                case 500:
                    errorTitle = 'Error del servidor';
                    errorMessage = 'Ocurrió un error interno del servidor. Por favor, inténtalo más tarde o contacta al soporte técnico.';
                    break;
                case 503:
                    errorTitle = 'Servicio no disponible';
                    errorMessage = 'El servicio no está disponible temporalmente. Por favor, inténtalo más tarde.';
                    break;
                default:
                    errorMessage = serverMessage || errorMessage;
            }
        }

        // Log detallado para debugging
        console.error('Error details:', {
            message: error.message,
            code: error.code,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            config: {
                url: error.config?.url,
                method: error.config?.method,
                headers: error.config?.headers
            }
        });

        // Si es error de conexión, mostrar botón de reintentar
        if (error.code === 'NETWORK_ERROR' || !error.response) {
            Swal.fire({
                title: errorTitle,
                html: errorMessage,
                icon: 'error',
                showCancelButton: true,
                confirmButtonText: 'Reintentar',
                cancelButtonText: 'Cerrar',
                confirmButtonColor: '#1f94c2',
                customClass: {
                    title: 'swal2-title-custom',
                    htmlContainer: 'swal2-html-container-custom'
                }
            }).then((result) => {
                if (result.isConfirmed) {
                    // Reintentar el envío
                    handleSubmit({ preventDefault: () => {} });
                }
            });
        } else {
            alertaGeneral({
                titulo: errorTitle,
                messageHtml: errorMessage,
                isError: true
            });
        }
    };

    // Funciones de validación auxiliares
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const isValidPhoneNumber = (phone) => {
        const phoneRegex = /^\d{10}$/;
        return phoneRegex.test(phone);
    };

    const isValidIdNumber = (id) => {
        const idRegex = /^\d{6,12}$/;
        return idRegex.test(id);
    };

    const validarTipoExpresionRegular = (e) => {
        switch (e.target.name) {
            case 'cellNumber':
                return validarInputExpresionRegular(e, /[^[0-9]/g);
            case 'idNumber':
                return validarInputExpresionRegular(e, /[^[0-9]/g);
            case 'fullName':
                return validarInputExpresionRegular(e, /[^[A-Za-z\s]/g);
            default:
                return e.target.value
        }
    }

    const handleChange = (e) => {
        const { name, type, files } = e.target;
        // Prevenir modificación solo de teleorientación (precio controles es configurable)
        if (name === 'precioTeleorientacion') {
            return; // No permitir cambios en este campo
        }
        
        // Si es la foto profesional, abrir el recortador
        if (name === 'foto_perfil' && type === 'file' && files && files[0]) {
            setImageToCrop(files[0]);
            setShowImageCropper(true);
            // Limpiar el input para que pueda seleccionar el mismo archivo de nuevo si cancela
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        // Diferir actualización de estado en inputs file para que el diálogo del SO cierre antes del re-render
        if (type === 'file') {
            const file = files?.[0];
            setTimeout(() => {
                setFormData(prevState => ({ ...prevState, [name]: file }));
            }, 0);
            return;
        }

        setFormData(prevState => ({
            ...prevState,
            [name]: validarTipoExpresionRegular(e),
        }));
    };
    
    const handleCropComplete = (croppedFile) => {
        setFormData(prevState => ({
            ...prevState,
            foto_perfil: croppedFile,
        }));
        setShowImageCropper(false);
        setImageToCrop(null);
    };
    
    const handleCropCancel = () => {
        setShowImageCropper(false);
        setImageToCrop(null);
    };

    const handleSelectChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handlePolicyChange = async (e) => {
        setHasPolicy(e.target.value);
        if (e.target.value === 'no') {
            // Modal distintivo con logo Redplika y oferta de asesoría en póliza
            const nombreVal = (formData.fullName || '').toString().replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
            const telefonoVal = (formData.cellNumber || '').toString().replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
            const cedulaVal = (formData.idNumber || '').toString().replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
            const logoSrc = typeof logoRedplika === 'string' ? logoRedplika : (logoRedplika?.default || '');
            const result = await Swal.fire({
                icon: false,
                title: '',
                html: `
                    <div class="contacto-modal-wrap">
                        <div class="contacto-modal-header">
                            ${logoSrc ? `<img src="${logoSrc}" alt="Logo" class="contacto-modal-logo" />` : ''}
                            <p class="contacto-modal-tagline">Te acompañamos con tu póliza</p>
                        </div>
                        <div class="contacto-modal-body">
                            <h3 class="contacto-modal-title">¿Necesitas una póliza?</h3>
                            <p class="contacto-modal-text">Si aún no tienes una póliza de responsabilidad civil profesional, podemos ayudarte a encontrar una opción adecuada para ti.</p>
                            <div class="contacto-modal-datos">
                                <div class="contacto-modal-dato">
                                    <label class="contacto-modal-label" for="contactoNombre">Nombre *</label>
                                    <input type="text" id="contactoNombre" class="contacto-modal-input" value="${nombreVal}" placeholder="Tu nombre completo" />
                                </div>
                                <div class="contacto-modal-dato">
                                    <label class="contacto-modal-label" for="contactoTelefono">Teléfono *</label>
                                    <input type="text" id="contactoTelefono" class="contacto-modal-input" value="${telefonoVal}" placeholder="Celular o teléfono" />
                                </div>
                                <div class="contacto-modal-dato">
                                    <label class="contacto-modal-label" for="contactoCedula">Número de identificación *</label>
                                    <input type="text" id="contactoCedula" class="contacto-modal-input" value="${cedulaVal}" placeholder="Número de identificación" />
                                </div>
                            </div>
                            <p class="contacto-modal-pregunta">¿Deseas que un asesor se comunique contigo?</p>
                        </div>
                    </div>
                `,
                showCancelButton: true,
                confirmButtonText: 'Sí, deseo ser contactado',
                cancelButtonText: 'No, gracias',
                focusConfirm: false,
                preConfirm: () => {
                    const nombre = document.getElementById('contactoNombre')?.value?.trim() || '';
                    const telefono = document.getElementById('contactoTelefono')?.value?.trim() || '';
                    const cedula = document.getElementById('contactoCedula')?.value?.trim() || '';
                    if (!nombre || !telefono || !cedula) {
                        Swal.showValidationMessage('Completa nombre, teléfono y número de identificación para continuar.');
                        return false;
                    }
                    return { nombre, telefono, cedula };
                },
                customClass: {
                    popup: 'swal-contacto-v2-popup',
                    htmlContainer: 'swal-contacto-v2-html',
                    confirmButton: 'swal-contacto-v2-confirm',
                    cancelButton: 'swal-contacto-v2-cancel'
                }
            });
            if (result.isConfirmed && result.value) {
                const { nombre, telefono, cedula } = result.value;
                try {
                    await apiClient.post('/api/home/solicitudContacto', {
                        nombre,
                        telefono,
                        cedula,
                        mensaje: 'Me interesa recibir asesoría sobre la compra de póliza de responsabilidad civil con REDPLIKA.'
                    });
                    await Swal.fire({
                        icon: 'success',
                        title: '¡Gracias!',
                        text: 'Pronto uno de nuestros aliados se comunicará contigo para asesorarte.',
                        confirmButtonText: 'Cerrar',
                        customClass: {
                            popup: 'swal-contacto-v2-popup',
                            confirmButton: 'swal-contacto-v2-confirm'
                        }
                    });
                } catch (error) {
                    console.error('Error al enviar solicitud de contacto:', error);
                    const errorMessage = error.response?.data?.message || 
                        (error.code === 'NETWORK_ERROR' ? 'No se pudo conectar con el servidor. Verifica tu conexión e inténtalo de nuevo.' : 
                         'No se pudo enviar la solicitud. Intenta nuevamente.');
                    
                    await Swal.fire({
                        icon: 'error',
                        title: 'Error al enviar solicitud',
                        text: errorMessage,
                        confirmButtonText: 'Cerrar'
                    });
                }
            }
            setShowContactField(false);
        } else {
            setShowContactField(false);
        }
    };

    return (
        <div className="w-full mx-auto p-8 bg-white">
            <button
                type="button"
                className="flex text-lg items-center px-2 py-1 font-semibold rounded-md text-[#1f94c2ff] bg-transparent focus:outline-none"
                onClick={() => setShow(false)}
            >
                <FontAwesomeIcon icon={faArrowLeft} className="flex items-center w-5 h-5 mr-3 shrink-0" /> Volver
            </button>

            <h2 className="text-2xl font-bold text-[#1f94c2ff] mb-4 text-center">Perfil Profesional</h2>
            
            {/* Mensaje informativo sobre documentos */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-[#1f94c2ff] rounded-xl p-4 sm:p-5 mb-6 shadow-md">
                <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-0.5">
                        <div className="w-10 h-10 bg-[#1f94c2ff] rounded-full flex items-center justify-center">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                    </div>
                    <div className="flex-1">
                        <h3 className="text-base sm:text-lg font-bold text-[#1f94c2ff] mb-3">
                            Antes de comenzar
                        </h3>
                        <div className="space-y-3 text-sm sm:text-base text-gray-700">
                            <div>
                                <p className="font-semibold text-gray-800">
                                    Ten tus documentos listos
                                </p>
                                <p>
                                    Durante el registro te solicitaremos documentos en formato PDF, JPG o PNG. Tenerlos disponibles te permitirá completar el proceso de forma ágil.
                                </p>
                            </div>
                            <div className="bg-white bg-opacity-60 rounded-lg p-3 border-l-4 border-orange-400">
                                <p className="font-semibold text-orange-700 mb-1">
                                    El registro se realiza una sola vez
                                </p>
                                <p className="text-sm text-gray-700">
                                    Verifica que tus datos y documentos estén completos y actualizados antes de enviar tu solicitud. Esto agilizará la validación de tu perfil.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => document.getElementById('inicio-registro')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                                className="inline-flex items-center px-5 py-2.5 bg-[#1f94c2ff] text-white rounded-md hover:bg-[#205087ff] text-sm font-medium"
                            >
                                Comenzar registro →
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Contrato de vinculación según el plan del profesional */}
            <div id="inicio-registro" className="mb-8 p-6 bg-white border-2 border-[#1f94c2ff] rounded-lg shadow-sm scroll-mt-6">
                <h3 className="text-xl font-semibold text-[#205087ff] mb-3 flex items-center">
                    <span className="mr-2">📄</span>
                    Contrato de vinculación
                </h3>
                {signedContractUrl && signedContractUrl !== '' ? (
                    <div className="space-y-2">
                        <p className="text-sm text-gray-700">
                            <strong>Contrato firmado</strong>
                            {signedContractDate && (
                                <> el {new Date(signedContractDate).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' })}</>
                            )}
                        </p>
                        <a
                            href={signedContractUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-2 bg-[#1f94c2ff] text-white rounded-md hover:bg-[#205087ff] text-sm"
                        >
                            Ver PDF firmado
                        </a>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <p className="text-sm text-gray-700">
                            Antes de completar tu perfil, debes firmar digitalmente el contrato de vinculación. Haz clic en &quot;Abrir y firmar contrato&quot; para consultar el documento completo y realizar tu firma en una nueva ventana.
                        </p>
                        <button
                            type="button"
                            onClick={() => setShowContratoModal(true)}
                            className="inline-flex items-center px-4 py-2 bg-[#1f94c2ff] text-white rounded-md hover:bg-[#205087ff] text-sm font-medium"
                        >
                            Abrir y firmar contrato →
                        </button>
                    </div>
                )}
            </div>

            {/* Modal: documento completo + firma digital (otra ventana) */}
            {showContratoModal && (
                <>
                <SignatureHelpModal
                    isOpen={showFirmaInstruccionesModal}
                    onClose={() => setShowFirmaInstruccionesModal(false)}
                    title="¿Cómo firmar el contrato?"
                />
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60" onClick={(e) => e.target === e.currentTarget && setShowContratoModal(false)}>
                    <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-[#1f94c2ff] text-white rounded-t-lg">
                            <div className="flex items-center gap-3 min-w-0">
                                <img src={logoRedplika} alt="Redplika" className="h-8 w-auto shrink-0 bg-white rounded px-1" />
                                <h3 className="text-lg font-semibold truncate">{contractTitle}</h3>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowContratoModal(false)}
                                className="p-1 rounded hover:bg-white/20 text-white"
                                aria-label="Cerrar"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                            <p className="text-sm text-gray-600">
                                Lee el documento completo a continuación. Al final, firma digitalmente para aceptar el contrato.
                            </p>
                            <div className="border border-gray-300 rounded-md overflow-y-auto bg-white p-6" style={{ maxHeight: '50vh', minHeight: 400 }}>
                                <ContractDocumentComponent />
                            </div>
                            <div className="pt-4 border-t-2 border-[#1f94c2ff]">
                                <p className="text-sm font-semibold text-[#205087ff] mb-2">Datos del firmante</p>
                                <p className="text-sm text-gray-600 mb-3">
                                    Ingresa tus datos tal como deben aparecer en el contrato.
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                                    <div className="md:col-span-2">
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Nombre completo *</label>
                                        <input
                                            type="text"
                                            value={contractModalNombre}
                                            onChange={(e) => setContractModalNombre(e.target.value)}
                                            className="w-full px-3 py-2 border border-[#9e9e9dff] rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#1f94c2ff]"
                                            placeholder="Nombre completo"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de documento *</label>
                                        <select
                                            value={contractModalIdType}
                                            onChange={(e) => setContractModalIdType(e.target.value)}
                                            className="w-full px-3 py-2 border border-[#9e9e9dff] rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#1f94c2ff]"
                                        >
                                            <option value="">Selecciona</option>
                                            <option value="CC">CC</option>
                                            <option value="TI">TI</option>
                                            <option value="CE">CE</option>
                                            <option value="PS">PS</option>
                                            <option value="DE">DE</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Número de identificación *</label>
                                        <input
                                            type="text"
                                            value={contractModalIdNumber}
                                            onChange={(e) => setContractModalIdNumber(e.target.value)}
                                            className="w-full px-3 py-2 border border-[#9e9e9dff] rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#1f94c2ff]"
                                            placeholder="Número"
                                        />
                                    </div>
                                </div>
                                <p className="text-sm font-semibold text-[#205087ff] mb-2">Firma digital</p>
                                <SignatureInstructionsPanel title="¿Cómo firmar el contrato?" />
                                <p className="text-xs text-gray-500 mb-3 text-center">
                                    Dibuja tu firma en el recuadro usando el mouse o tu dedo
                                </p>
                                <div ref={signatureContainerRef} data-signature-canvas className="border-2 border-[#9e9e9dff] rounded-md bg-white w-full overflow-hidden" style={{ touchAction: 'none' }}>
                                    <SignatureCanvas
                                        ref={sigCanvasRefContrato}
                                        canvasProps={{
                                            className: 'rounded-md block',
                                            width: signatureCanvasSize.width,
                                            height: signatureCanvasSize.height,
                                            style: { width: '100%', height: signatureCanvasSize.height, display: 'block' }
                                        }}
                                        penColor="black"
                                        backgroundColor="rgb(255, 255, 255)"
                                        clearOnResize={false}
                                    />
                                </div>
                                <div className="flex flex-wrap gap-2 mt-3">
                                    <button
                                        type="button"
                                        onClick={() => sigCanvasRefContrato.current?.clear()}
                                        className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm"
                                    >
                                        Limpiar firma
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleFirmarContrato}
                                        disabled={contractSigning}
                                        className="px-4 py-2 bg-[#1f94c2ff] text-white rounded-md hover:bg-[#205087ff] disabled:opacity-50 text-sm font-medium"
                                    >
                                        {contractSigning ? 'Guardando...' : 'Firmar contrato'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowContratoModal(false)}
                                        className="px-3 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm"
                                    >
                                        Cerrar (sin firmar)
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                </>
            )}

            <form className="space-y-6" onSubmit={handleSubmit} encType="multipart/form-data">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                    {/* Nombre Completo */}
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Nombre Completo *</label>
                        <input
                            type="text"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            placeholder="Nombre Completo"
                            required
                        />
                    </div>

                    {/* Tipo de Documento de Identidad */}
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Tipo de Documento de Identidad *</label>
                        <select
                            name="idType"
                            value={formData.idType}
                            onChange={handleSelectChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            required
                        >
                            <option value="cc">CC - Cédula de ciudadanía</option>
                            <option value="ti">TI - Tarjeta de identidad</option>
                            <option value="ce">CE - Cédula de extranjería</option>
                            <option value="ps">PS - Pasaporte</option>
                            <option value="de">DE - Documento extranjero</option>
                        </select>
                    </div>

                    {/* Número de Documento de Identidad */}
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Número de Documento de Identidad *</label>
                        <input
                            type="text"
                            name="idNumber"
                            value={formData.idNumber}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            placeholder="Número de Documento de Identidad"
                            required
                        />
                    </div>

                    {/* Correo Electrónico */}
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Correo Electrónico *</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            placeholder="Correo Electrónico"
                            required
                        />
                    </div>

                    {/* Número de Celular */}
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Número de Celular *</label>
                        <input
                            type="text"
                            name="cellNumber"
                            value={formData.cellNumber}
                            onChange={handleChange}
                            maxLength={10}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            placeholder="Número de Celular"
                            required
                        />
                    </div>

                    {/* Foto profesional digital */}
                    <div className="col-span-1 my-2">
                        <label className="block text-md font-semibold text-[#70706eff] flex items-center gap-2">
                            Foto Profesional Digital {doctor.urlProfessionalPhoto && doctor.urlProfessionalPhoto !== '' ? '' : '*'}
                            {(doctor.urlProfessionalPhoto && doctor.urlProfessionalPhoto !== '') || (formData.foto_perfil && (formData.foto_perfil instanceof File || formData.foto_perfil?.name)) ? (
                                <span className="inline-flex items-center text-green-600" title="Foto de perfil definida">
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                </span>
                            ) : null}
                        </label>
                        {doctor.urlProfessionalPhoto && doctor.urlProfessionalPhoto !== '' && (
                            <p className="text-sm text-gray-600 mb-2">
                                Ya tienes una foto de perfil. Puedes cambiarla subiendo una nueva imagen o mantener la actual.
                            </p>
                        )}
                            <div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    name="foto_perfil"
                                    className="hidden"
                                    onChange={handleChange}
                                    accept=".jpg,.jpeg,.png"
                                />
                                <div
                                    className="relative cursor-pointer group flex items-center mt-1 w-full space-x-1 px-3 py-2 border-b border-[#9e9e9dff]"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <label
                                        className="flex-shrink-0 cursor-pointer whitespace-nowrap bg-[#1f94c2ff] text-white px-2 py-1 rounded-sm"
                                    >
                                        Elegir archivo
                                    </label>
                                    <span className="text-[#9e9e9dff] cursor-pointer flex-grow overflow-hidden text-ellipsis whitespace-nowrap">
                                        {formData.foto_perfil?.name || "No se ha seleccionado archivo"}
                                    </span>
                                    <span className="absolute left-1/2 -translate-x-1/2 cursor-pointer -top-5 w-max bg-gray-800 text-white text-xs p-1 opacity-0 group-hover:opacity-100 transition">
                                        {formData.foto_perfil?.name || (doctor.urlProfessionalPhoto && doctor.urlProfessionalPhoto !== '' ? "Mantener foto actual" : "No se ha seleccionado ningún archivo")}
                                    </span>
                                </div>
                            </div>
                    </div>

                    {/* Documento (Adjuntar) */}
                    {doctor.urlIdDocument === '' && (
                        <div className="col-span-1 my-2">
                            <label className="block text-md font-semibold text-[#70706eff]">Documento Identidad (PDF o Imagen) *</label>
                            <InputFile
                                key="cedulaPDF"
                                name="cedulaPDF"
                                onChange={handleChange}
                                fileName={formData.cedulaPDF?.name}
                                accept=".pdf,.jpg,.jpeg,.png"
                            />
                        </div>
                    )}

                    {/* Diploma o Acta de Grado de Pregrado */}
                    <div className="col-span-1 my-2">
                        <label className="block text-md font-semibold text-[#70706eff]">Diploma o Acta de Grado de Pregrado (PDF o Imagen) *</label>
                        <InputFile
                            key="diploma_acta_grado"
                            name="diploma_acta_grado"
                            onChange={handleChange}
                            fileName={formData.diploma_acta_grado?.name}
                            accept={".pdf,.jpg,.jpeg,.png"}
                        />
                    </div>

                    {/* Tarjeta Profesional */}
                    <div className="col-span-1 my-2">
                        <label className="block text-md font-semibold text-[#70706eff]">Tarjeta Profesional (PDF o Imagen) *</label>
                        <InputFile
                            key="tarjeta_profesional"
                            name="tarjeta_profesional"
                            onChange={handleChange}
                            fileName={formData.tarjeta_profesional?.name}
                            accept={".pdf,.jpg,.jpeg,.png"}
                        />
                    </div>

                    {/* Diploma o Acta de Grado de Especialización */}
                    <div className="col-span-1 my-2">
                        <label className="block text-md font-semibold text-[#70706eff]">Diploma o Acta de Grado de Especialización (si aplica)</label>
                        <InputFile
                            key="diploma_especializacion"
                            name="diploma_especializacion"
                            onChange={handleChange}
                            fileName={formData.diploma_especializacion?.name}
                            accept={".pdf,.jpg,.jpeg,.png"}
                        />
                    </div>

                    {/* Homologación de Estudios */}
                    <div className="col-span-1 my-2">
                        <label className="block text-md font-semibold text-[#70706eff]">Homologación de Estudios (si aplica)</label>
                        <InputFile
                            key="homologacion_estudios"
                            name="homologacion_estudios"
                            onChange={handleChange}
                            fileName={formData.homologacion_estudios?.name}
                            accept={".pdf,.jpg,.jpeg,.png"}
                        />
                    </div>

                    {(doctorServiceStr.includes('medicina') || doctorServiceStr.includes('psicología') || doctorServiceStr.includes('terapia') ||
                        doctorServiceStr.includes('terapeuta') || doctorServiceStr.includes('nutrición') || doctorServiceStr.includes('enfermería') || doctorServiceStr.includes('fonoaudiología')) && (
                            <>
                                {/* Firma del profesional */}
                                <div className="col-span-1 my-2">
                                    <label className="block text-md font-semibold text-[#70706eff] flex items-center">Firma del profesional (300px * 150px) *
                                        <button type="button" className="ml-2 text-[#f79865ff] focus:outline-none" onClick={() => Swal.fire({
                                            title: 'Recomendación para la firma',
                                            html: 'La imagen debe contener tu firma y sello, ser legible y tener fondo blanco.',
                                            icon: 'info',
                                            confirmButtonText: 'Entendido',
                                            customClass: { title: 'swal2-title-custom', htmlContainer: 'swal2-html-container-custom' }
                                        })}>
                                            <FontAwesomeIcon icon={faInfoCircle} />
                                        </button>
                                    </label>
                                    <InputFile
                                        key="firma_profesional"
                                        name="firma_profesional"
                                        onChange={handleChange}
                                        fileName={formData.firma_profesional?.name}
                                        accept={".jpg,.jpeg,.png"}
                                        onBeforeClick={async () => {
                                            await Swal.fire({
                                                title: 'Recomendación para la firma',
                                                html: 'La imagen debe contener tu firma y sello, ser legible y tener fondo blanco.',
                                                icon: 'info',
                                                confirmButtonText: 'Entendido',
                                                customClass: { title: 'swal2-title-custom', htmlContainer: 'swal2-html-container-custom' }
                                            });
                                            return true;
                                        }}
                                    />
                                </div>
                                {/* ReThus */}
                                <div className="col-span-1 my-2">
                                    <label className="block text-md font-semibold text-[#70706eff]">ReThus (PDF o Imagen) * </label>
                                    <InputFile
                                        key="rethus"
                                        name="rethus"
                                        onChange={handleChange}
                                        fileName={formData.rethus?.name}
                                        accept={".pdf,.jpg,.jpeg,.png"}
                                    />
                                </div>

                                {/* Select para póliza */}
                                <div className="col-span-1 my-2">
                                    <label className="block text-md font-semibold text-[#70706eff]">¿Tiene póliza de responsabilidad civil vigente? *</label>
                                    <select
                                        name="hasPolicy"
                                        value={hasPolicy}
                                        onChange={handlePolicyChange}
                                        className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                                        required
                                    >
                                        <option value="">Selecciona una opción</option>
                                        <option value="si">Sí</option>
                                        <option value="no">No</option>
                                    </select>
                                </div>
                                {/* Si tiene póliza, mostrar campos (obligatorios: número y documento) */}
                                {hasPolicy === 'si' && (
                                    <>
                                <div className="col-span-1 my-2">
                                    <label className="block text-md font-semibold text-[#70706eff]">Póliza de Responsabilidad Civil Vigente (Número y PDF o Imagen) *</label>
                                    <input
                                        type="text"
                                        name="civilLiabilityPolicyNumber"
                                        value={formData.civilLiabilityPolicyNumber}
                                        onChange={handleChange}
                                        className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                                        required={hasPolicy === 'si'}
                                        placeholder="Número de Póliza"
                                    />
                                    <InputFile
                                        key="poliza_responsabilidad_civil"
                                        name="poliza_responsabilidad_civil"
                                        onChange={handleChange}
                                        fileName={formData.poliza_responsabilidad_civil?.name}
                                        accept={".pdf,.jpg,.jpeg,.png"}
                                    />
                                </div>
                                    </>
                                )}
                                {/* Si no tiene póliza, mostrar campo de contacto */}
                                {showContactField && hasPolicy === 'no' && (
                                    <div className="col-span-1 my-2">
                                        <label className="block text-md font-semibold text-[#70706eff]">Contáctanos</label>
                                        <input
                                            type="text"
                                            name="contactNumber"
                                            value={contactNumber}
                                            onChange={e => setContactNumber(e.target.value)}
                                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                                            placeholder="Deja tu número para que te contactemos"
                                        />
                                    </div>
                                )}
                            </>
                        )}
                </div>

                {/* Botón de Enviar */}
                <div className="flex justify-center mt-6">
                    <button
                        type="submit"
                        className="text-[#205087ff] text-md font-medium px-4 py-1 border border-[#6e787aff] bg-[#f79865ff] rounded-md focus:outline-none"
                    >
                        Enviar Perfil
                    </button>
                </div>
            </form >
            
            {/* Modal de recorte de imagen */}
            {showImageCropper && imageToCrop && (
                <ImageCropper
                    imageFile={imageToCrop}
                    onCropComplete={handleCropComplete}
                    onCancel={handleCropCancel}
                />
            )}
        </div >

    );
    function reemplazarKeys() {
        const keys = {
            fullName: 'Nombre Completo',
            idType: 'Tipo de Documento de Identidad',
            idNumber: 'Número de Documento de Identidad',
            cedulaPDF: 'Documento Identidad',
            email: 'Correo Electrónico',
            cellNumber: 'Número de Celular',
            diploma_acta_grado: 'Diploma o Acta de Grado de Pregrado',
            diploma_especializacion: 'Diploma o Acta de Grado de Especialización',
            homologacion_estudios: 'Homologación de Estudios',
            tarjeta_profesional: 'Tarjeta Profesional',
            rethus: 'ReThus',
            civilLiabilityPolicyNumber: 'Póliza de Responsabilidad Civil (Número)',
            poliza_responsabilidad_civil: 'Póliza de Responsabilidad Civil',
            foto_perfil: 'Foto Profesional Digital',
            firma_profesional: 'Firma Profesional',
        }
        return keys;
    }
}

export default CompleterProfileDoctor;
